import { Injectable } from '@angular/core';
import { Group } from '@wb/core/interfaces/group';
import { gql, Query } from 'apollo-angular';

export interface GroupsResponse {
  groups: {
    code: string;
    success: boolean;
    message: string;
    groups: Group[];
    count: number;
  };
}

export interface GroupsQueryVariables {
  where?: GroupFilter;
  limit?: number;
  offset?: number;
  searchInput?: string | null;
  sortDirection?: string;
  sortColumn?: string;
}

export interface GroupFilter {
  uid?: string;
  accountUids?: string[];
  name?: string;
  default_account_id?: number;
  deleted_at?: string;
  created_at?: string;
  updated_at?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GroupsGQL extends Query<GroupsResponse, GroupsQueryVariables> {
  document = gql(`
  query groups (
    $where: ModelGroupFilterInput,
    $limit: Int,
    $offset: Int,
    $sortColumn: String,
    $sortDirection: ModelSortDirection,
    $searchInput: String
  ) {
    groups (
      where: $where,
      limit: $limit,
      offset: $offset,
      sortColumn: $sortColumn,
      sortDirection: $sortDirection,
      searchInput: $searchInput
    ) {
        code
        success
        message
        count
        groups {
          uid
          name
          default_account_id
          dealershipCount
          userCount
          deleted_at
          created_at
          updated_at
        }
      }
    }
  `);
}

export interface GroupResponse {
  group: {
    code: string;
    success: boolean;
    message: string;
    group: Group;
  };
}

export interface GroupQueryVariables {
  uid: string;
}

@Injectable({
  providedIn: 'root',
})
export class GroupGQL extends Query<GroupResponse, GroupQueryVariables> {
  document = gql(`
    query group ($uid: ID!) {
      group (uid: $uid) {
        code
        success
        message
        group {
          uid
          name
          default_account_id
          deleted_at
          created_at
          updated_at
        }
      }
    }
  `);
}

export interface GroupAccountsResponse {
  groupAccounts: {
    code: string;
    success: boolean;
    message: string;
    accounts: [GroupAccount];
  };
}

export interface GroupAccount {
  uid: string;
  account: string;
}

@Injectable({
  providedIn: 'root',
})
export class GroupAccountsGQL extends Query<GroupAccountsResponse, GroupQueryVariables> {
  document = gql(`
    query groupAccounts ($uid: ID!) {
      groupAccounts (uid: $uid) {
        code
        success
        message
        accounts {
          uid
          account
        }
      }
    }
  `);
}

export interface GroupUsersResponse {
  groupUsers: {
    code: string;
    success: boolean;
    message: string;
    groupAccountUsers: [GroupAccountUser];
  };
}

export interface GroupAccountUser {
  account: GroupAccount;
  users: [GroupUser];
}

export interface GroupUser {
  uid: string;
  first_name: string;
  last_name: string;
  disabled: boolean;
  primaryAccount: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GroupUsersGQL extends Query<GroupUsersResponse, GroupQueryVariables> {
  document = gql(`
    query groupUsers ($uid: ID!) {
      groupUsers (uid: $uid) {
        code
        success
        message
        groupAccountUsers {
          account {
            uid
            account
          }
          users {
            uid
            first_name
            last_name
            disabled
            primaryAccount
          }
        }
      }
    }
  `);
}

export interface AvailableGroupAccountsResponse {
  availableGroupAccounts: {
    code: string;
    success: boolean;
    message: string;
    groupAccounts: [GroupAccount];
  };
}

@Injectable({
  providedIn: 'root',
})
export class AvailableGroupAccountsGQL extends Query<AvailableGroupAccountsResponse> {
  document = gql(`
    query availableGroupAccounts {
      availableGroupAccounts {
        code
        success
        message
        groupAccounts {
          uid
          account
        }
      }
    }
  `);
}
